// Push alerts on the driver's phone — capability check, enable/disable, and the tile badge.
//
// THE ONE THING TO KNOW: on iPhone, Web Push and the Home Screen badge only work when the
// portal was ADDED TO THE HOME SCREEN and opened from that icon (iOS 16.4+, March 2023).
// In a Safari tab `Notification` and `PushManager` simply don't exist, so `capability()`
// reports `needsInstall` and the portal shows the Add-to-Home-Screen instructions instead
// of an Enable button that could never work. Android/Chrome has no such restriction.
//
// Nothing here ever throws at the caller: a driver whose phone won't take alerts must
// still be able to run their shift.
//
// `app` says which Home Screen app this subscription belongs to — 'driver' (HP Driver, the
// portal) or 'engine' (HP Engine, the back office). They install as separate apps with
// separate storage, so someone who has both gets two subscriptions; the server addresses
// each alert to one of them so nobody gets the same case twice on two tiles.

const API = '/.netlify/functions/push-sb';

// A stable id for THIS phone, so the server can tell "the same handset re-subscribing"
// from "a second device". The browser issues a brand-new endpoint whenever a subscription
// is re-created — toggling alerts, a service-worker update, iOS reissuing on its own — and
// the old one often still works, which is how one iPhone ended up ringing twice for a
// single case. localStorage survives all of that. (Deleting and re-adding the Home Screen
// icon does wipe it, but that also kills the old subscription, so the server prunes it on
// the next send.)
function deviceId() {
  try {
    let id = window.localStorage.getItem('hp_push_device');
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      window.localStorage.setItem('hp_push_device', id);
    }
    return id;
  } catch (e) { return ''; }   // private mode / storage blocked — fall back to endpoint-only
}

const api = (action, payload) => fetch(API, {
  method: 'POST', headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ action, ...payload }),
}).then((r) => r.json()).catch(() => ({ success: false, error: 'offline' }));

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS reports as Mac

// Opened from the Home Screen icon rather than a browser tab.
export const isInstalled = () =>
  window.navigator.standalone === true ||
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

// What can this device actually do? Drives what the portal offers.
//   ok           — everything is here, we can ask for permission
//   needsInstall — iPhone in a Safari tab: Add to Home Screen first, then come back
//   unsupported  — an old browser; nothing to offer
//   denied       — they said No; only iOS Settings can undo it, we can't re-prompt
export function capability() {
  const hasSW = 'serviceWorker' in navigator;
  const hasPush = 'PushManager' in window;
  const hasNotif = 'Notification' in window;
  if (!hasSW || !hasPush || !hasNotif) {
    return { state: (isIOS() && !isInstalled()) ? 'needsInstall' : 'unsupported', ios: isIOS() };
  }
  if (isIOS() && !isInstalled()) return { state: 'needsInstall', ios: true };
  const permission = Notification.permission;
  if (permission === 'denied') return { state: 'denied', ios: isIOS(), permission };
  return { state: 'ok', ios: isIOS(), permission };
}

// base64url VAPID key → the Uint8Array the subscribe() call wants.
function toKeyBytes(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = window.atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

let swReady = null;
export function registerSW() {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  if (!swReady) {
    swReady = navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then(() => navigator.serviceWorker.ready)
      .catch(() => { swReady = null; return null; });
  }
  return swReady;
}

// Ask for permission and register this device. MUST be called from a real tap — iOS
// throws away a permission prompt that isn't inside a user gesture.
export async function enableAlerts(app = 'driver') {
  const cap = capability();
  if (cap.state !== 'ok') return { ok: false, state: cap.state };

  try {
    const { publicKey, configured } = await api('key', {});
    if (!configured || !publicKey) return { ok: false, error: 'Alerts are not set up on the server yet.' };

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, state: permission === 'denied' ? 'denied' : 'dismissed' };

    const reg = await registerSW();
    if (!reg) return { ok: false, error: 'This browser would not start the alert worker.' };

    // An existing subscription is reused rather than replaced: re-subscribing with the
    // same key returns the same endpoint, and the server treats it as an upsert.
    const sub = await reg.pushManager.getSubscription() ||
      await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKeyBytes(publicKey) });

    const r = await api('subscribe', { subscription: sub.toJSON(), ua: navigator.userAgent, deviceId: deviceId(), app });
    if (!r.success) return { ok: false, error: r.error || 'Could not register this phone.' };
    return { ok: true, devices: r.devices };
  } catch (e) {
    return { ok: false, error: e.message || 'Could not turn on alerts.' };
  }
}

export async function disableAlerts() {
  try {
    const reg = await registerSW();
    const sub = reg && await reg.pushManager.getSubscription();
    if (sub) {
      await api('unsubscribe', { endpoint: sub.endpoint });
      await sub.unsubscribe().catch(() => {});
    }
    return { ok: true };
  } catch (e) { return { ok: false, error: e.message }; }
}

// Is this phone registered right now? Checks the browser AND the server, because the two
// drift: deleting and re-adding the Home Screen icon silently voids the subscription while
// the server still lists a dead endpoint.
export async function alertStatus() {
  const cap = capability();
  if (cap.state !== 'ok') return { ...cap, on: false };
  try {
    const reg = await registerSW();
    const sub = reg && await reg.pushManager.getSubscription();
    if (!sub) return { ...cap, on: false };
    const r = await api('status', { endpoint: sub.endpoint });
    return { ...cap, on: !!r.thisDevice, devices: r.devices || 0, configured: r.configured !== false };
  } catch (e) { return { ...cap, on: false }; }
}

// Called on every portal launch. Tops up a subscription that has lapsed — a browser-rotated
// endpoint would otherwise stop someone's alerts with no visible sign at either end.
//
// It ONLY ever re-registers a device the server already holds a subscription for. It must
// never create one, because `Notification.permission` is granted per SITE and can already
// be 'granted' from something else entirely — which is exactly what happened: a driver who
// had never been offered the switch was silently subscribed on their next launch. Consent
// lives in the device id on the server, not in a browser permission flag.
export async function syncAlerts(app = 'driver') {
  try {
    if (capability().state !== 'ok' || Notification.permission !== 'granted') return;
    const reg = await registerSW();
    if (!reg) return;
    const existing = await reg.pushManager.getSubscription();
    const st = await api('status', { endpoint: existing && existing.endpoint, deviceId: deviceId() });
    if (!st.thisDevice && !st.knownDevice) return;      // never opted in on this device
    const { publicKey, configured } = await api('key', {});
    if (!configured || !publicKey) return;
    const sub = existing ||
      await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKeyBytes(publicKey) });
    await api('subscribe', { subscription: sub.toJSON(), ua: navigator.userAgent, deviceId: deviceId(), app });
  } catch (e) { /* silent — this is a background top-up, not something to bother a driver with */ }
}

// The number on the Home Screen tile. Same install requirement as the banner; everywhere
// else it's a no-op. Note iOS never wakes the app on its own, so while the app is CLOSED
// this only changes when a push arrives and the service worker sets it.
export function setTileBadge(n) {
  try {
    if (typeof n !== 'number' || n < 0) return;
    if (n > 0 && navigator.setAppBadge) navigator.setAppBadge(n);
    else if (navigator.clearAppBadge) navigator.clearAppBadge();
  } catch (e) { /* unsupported */ }
}
